import { useState } from 'react';
import {
  Box, Stack, Typography, Alert, Button, Chip, Grid, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { setPreflight, addAmendment, clearUnlocks, setStage } from '../../../../redux/pricingWizardSlice';
import { useExecuteCommitMutation, useLazyRunPreFlightQuery } from '../../../../redux/wizardApiSlice';
import { formatDate } from '../helpers/rateEngine';


function runPreflight(wizard) {
  const issues = [];
  wizard.rateCard.rows.forEach((r) => {
    if (r.max <= r.min) issues.push({ acc: r.account_code, msg: `Zone ${r.zone}: Max(${r.max}) ≤ Min(${r.min})` });
    if (r.tax !== 0 && r.tax !== 18) issues.push({ acc: r.account_code, msg: `Tax must be 0 or 18 (got ${r.tax})` });
    if (r.rto_pct > 100) issues.push({ acc: r.account_code, msg: 'RTO (as % of forward) > 100' });
    if (r.price < 0) issues.push({ acc: r.account_code, msg: 'Negative price' });
  });
  const wc = wizard.agreement.weight_ceiling;
  if (wc) {
    const byAccZone = {};
    wizard.rateCard.rows.forEach((r) => { const key = `${r.account_code}|${r.zone}`; if (!byAccZone[key]) byAccZone[key] = 0; if (+r.max > byAccZone[key]) byAccZone[key] = +r.max; });
    Object.keys(byAccZone).forEach((key) => { const [acc, z] = key.split('|'); if (byAccZone[key] < wc) issues.push({ acc, msg: `Zone ${z}: last slab max (${byAccZone[key]} GMS) < weight ceiling (${wc} GMS)` }); });
  }
  if (!wizard.invoice_ceiling || +wizard.invoice_ceiling <= 0) issues.push({ acc: '—', msg: 'Invoice-value ceiling is not set (Stage 5)' });
  if (wizard.tc.blended.cod_structure === 'tiered_weight' && wc) {
    const lastMax = Math.max(...wizard.tc.blended.cod_tiers_weight.map((t) => +t.max || 0));
    if (lastMax < wc) issues.push({ acc: '—', msg: `Weight Slab COD: final slab (${lastMax} GMS) < weight ceiling (${wc} GMS)` });
  }
  return { ok: issues.length === 0, issues };
}

export default function Stage7_Commit() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wizard = useSelector((s) => s.pricingWizard);
  const currentUser = useSelector((s) => s.user?.userInfo?.fullname || s.user?.userInfo?.name || s.user?.userInfo?.sub || 'Admin');
  const { preflight, rateCard, agreement, resolvedServices, kyc, merchant, effective_mode, effective_date } = wizard;
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executeCommit, { isLoading: isCommitting }] = useExecuteCommitMutation();
  const [runPreFlight] = useLazyRunPreFlightQuery();
  const accCount = [...new Set(rateCard.rows.map((r) => r.account_code))].length;
  const kycApproved = kyc.overallStatus === 'approved';

  const onRun = async () => {
    setRunning(true);
    try {
      const result = await runPreFlight(wizard.merchantId);
      if (result.data) {
        dispatch(setPreflight({
          ok: result.data.clean,           // schema + rule checks only — independent of KYC gate
          canCommit: result.data.canCommit,
          commitBlockReason: result.data.commitBlockReason,
          issues: (result.data.checks?.errors || []).map((e) => {
            if (typeof e === 'string') {
              // Parse "ACCOUNT_CODE/zone=1/row=42: price is required"
              const m = e.match(/^([^/]+)\/zone=(\d+)\/row=(\d+):\s*(.+)$/);
              return m ? { acc: m[1], zone: m[2], row: m[3], msg: m[4] } : { acc: '—', msg: e };
            }
            return { acc: e.accountCode || '—', msg: e.message || String(e) };
          }),
          summary: result.data.summary,
          emailPreview: result.data.emailPreview,
        }));
      }
    } catch (err) {
      console.error('Pre-flight error:', err);
    }
    setRunning(false);
  };

  const doCommit = async () => {
    if (wizard.editMode || Object.keys(wizard.unlocks || {}).length) {
      dispatch(addAmendment({ by: currentUser, kind: wizard.editMode ? 'amendment' : 'post-unlock-recommit', unlocked_steps: Object.keys(wizard.unlocks || {}) }));
      dispatch(clearUnlocks());
    }
    try {
      await executeCommit().unwrap();
      setConfirmOpen(false);
      enqueueSnackbar(wizard.editMode ? 'Amendment saved · new rate-card version' : 'Rate card committed to ProShip', { variant: 'success' });
      dispatch(setStage(8));
    } catch (err) {
      enqueueSnackbar(err?.data?.message || err?.message || 'Commit failed — please retry', { variant: 'error' });
    }
  };

  // Email preview — use server data when available (from pre-flight response)
  const ep = preflight?.emailPreview;
  const emailPreview = ep
    ? `Completed by : ${ep.completedBy || currentUser}
Merchant     : ${ep.merchantName || merchant?.brand || '—'} (${ep.merchantExternalId || merchant?.code || '—'})
Rate card    : ${ep.rateCardVersionLabel || 'v[on commit]'}
Effective    : ${ep.effectiveDateDisplay || formatDate(effective_date)}
Service types: ${ep.serviceTypesDisplay || resolvedServices.map((s) => s.id).join(', ')}
Acct codes   : ${ep.accountCodesCount ?? accCount}
GST stance   : ${ep.gstStance || agreement.gst_stance}`
    : `Completed by : ${currentUser}
Merchant     : ${merchant?.brand || '—'} (${merchant?.code || '—'})
Effective    : ${formatDate(effective_date)} ${effective_mode === 'scheduled' ? '(scheduled)' : '(immediate)'}
Service types: ${resolvedServices.map((s) => s.id).join(', ')}
Acct codes   : ${accCount}
GST stance   : ${agreement.gst_stance}

Note: Email recipients and subject are configured in merchant_config.pricingSetupEmail`;

  return (
    <Box>
      <Alert severity="info" icon={<span>🔎</span>} sx={{ mb: 2 }}>
        Pre-flight runs schema + business-rule validation on every row. On a clean pass, the wizard writes natively into
        <Chip component="span" label="rate_card_version" size="small" sx={{ mx: 0.25, height: 18, fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} /> +
        <Chip component="span" label="rate_card_row" size="small" sx={{ mx: 0.25, height: 18, fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} /> +
        <Chip component="span" label="terms_and_conditions" size="small" sx={{ mx: 0.25, height: 18, fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} /> in a single transaction.
      </Alert>

      {!preflight && (
        <Button variant="contained" disabled={running} startIcon={running ? <CircularProgress size={16} color="inherit" /> : null} onClick={onRun}>
          {running ? `Running pre-flight on ${rateCard.rows.length} rows…` : '▶ Run Pre-flight'}
        </Button>
      )}

      {preflight && preflight.ok && (
        <>
          <Alert severity="success" icon={<span>✅</span>}>Pre-flight <b>CLEAN</b> · all 17-column schema checks passed · all business-rule checks passed</Alert>

          {/* Commit block reason — shown when checks pass but canCommit is still false (e.g. KYC gate) */}
          {!preflight.canCommit && preflight.commitBlockReason && (
            <Alert severity="warning" icon={<span>🔒</span>} sx={{ mt: 1 }}>
              <b>Commit blocked:</b> {preflight.commitBlockReason}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {[
              ['Account codes', preflight.summary?.accountCodes ?? accCount],
              ['Rate-card rows', preflight.summary?.rateCardRows ?? rateCard.rows.length],
              ['Effective', preflight.summary?.effectiveMode === 'IMMEDIATE' ? 'Immediate' : formatDate(preflight.summary?.effectiveDate || effective_date)],
            ].map(([k, v]) => (
              <Grid item xs={12} md={4} key={k}><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10 }}>{k}</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{v}</Typography></Paper></Grid>
            ))}
          </Grid>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>📧 Email notification preview</Typography>
            <Chip size="small" color="info" variant="outlined" label="email config in merchant_config collection" />
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
            <Box component="pre" sx={{ m: 0, fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'text.primary' }}>{emailPreview}</Box>
          </Paper>

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={!preflight.canCommit || isCommitting}
              onClick={() => setConfirmOpen(true)}
            >
              {isCommitting ? 'Committing…' : wizard.editMode ? '💾 Save Amendment' : 'Commit to ProShip'}
            </Button>
            {!preflight.canCommit && (
              <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                ⚠ {preflight.commitBlockReason || `KYC ${kyc.overallStatus} — Commit unlocks once Approved`}
              </Typography>
            )}
          </Stack>
        </>
      )}

      {preflight && !preflight.ok && (
        <>
          <Alert severity="error" icon={<span>❌</span>}>
            Pre-flight <b>FAILED</b> · {preflight.issues.length} issue{preflight.issues.length !== 1 ? 's' : ''} · commit blocked
            {preflight.issues.length > 40 && <> · showing first 40 of {preflight.issues.length}</>}
          </Alert>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 700, fontSize: 11 } }}>
                  <TableCell>Account Code</TableCell>
                  <TableCell>Zone</TableCell>
                  <TableCell>Row</TableCell>
                  <TableCell>Issue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preflight.issues.slice(0, 40).map((issue, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{issue.acc}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{issue.zone ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}>{issue.row ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{issue.msg}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" onClick={() => dispatch(setStage(4))}>Fix in Rate Card Builder →</Button>
            <Button variant="outlined" onClick={onRun}>Re-run pre-flight</Button>
          </Stack>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm commit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>This action is logged in the immutable audit trail. Pre-flight passed — confirm one final time.</Typography>
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />}>Stages 2-6 will freeze on commit. Admin unlock is required for post-commit edits.</Alert>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doCommit} disabled={isCommitting} startIcon={isCommitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {isCommitting ? 'Committing…' : 'Yes, commit to ProShip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
